// src/pages/PlayPuzzleCard.jsx
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import VictoryOverlay from '../components/VictoryOverlay';

const canvasWidth = 400;

const PlayPuzzleCard = () => {
  const { id } = useParams();
  const [cardData, setCardData] = useState(null);
  const [slots, setSlots] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [completed, setCompleted] = useState(false);
  const [aspectRatio, setAspectRatio] = useState(1);
  const [showPreview, setShowPreview] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCard = async () => {
      try {
        const ref = doc(db, 'puzzleCards', id);
        const snap = await getDoc(ref);
        if (snap.exists()) {
          setCardData(snap.data());
        } else {
          setError('Puzzle not found.');
        }
      } catch (err) {
        console.error(err);
        setError('Error loading puzzle data.');
      }
    };
    fetchCard();
  }, [id]);

  useEffect(() => {
    if (!cardData?.imageUrl) return;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = cardData.imageUrl;

    img.onload = () => {
      const ratio = img.width / img.height;
      setAspectRatio(ratio);

      const gridSize = cardData.gridSize || 3;
      const canvasHeight = canvasWidth / ratio;
      const pieceWidth = canvasWidth / gridSize;
      const pieceHeight = canvasHeight / gridSize;

      const canvas = document.createElement('canvas');
      canvas.width = canvasWidth;
      canvas.height = canvasHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, canvasWidth, canvasHeight);

      const pieces = [];
      for (let y = 0; y < gridSize; y++) {
        for (let x = 0; x < gridSize; x++) {
          const pieceCanvas = document.createElement('canvas');
          pieceCanvas.width = pieceWidth;
          pieceCanvas.height = pieceHeight;
          const pctx = pieceCanvas.getContext('2d');
          pctx.drawImage(
            canvas,
            x * pieceWidth,
            y * pieceHeight,
            pieceWidth,
            pieceHeight,
            0,
            0,
            pieceWidth,
            pieceHeight
          );
          pieces.push({ id: y * gridSize + x, img: pieceCanvas.toDataURL() });
        }
      }

      const scrambled = [...pieces].sort(() => Math.random() - 0.5);
      setSlots(scrambled);
    };
  }, [cardData]);

  const handleSwap = (index) => {
    if (selectedIndex === null) {
      setSelectedIndex(index);
    } else {
      const newSlots = [...slots];
      [newSlots[selectedIndex], newSlots[index]] = [newSlots[index], newSlots[selectedIndex]];
      setSlots(newSlots);
      setSelectedIndex(null);

      if (newSlots.every((p, i) => p.id === i)) {
        setCompleted(true);
      }
    }
  };

  if (error) return <div>{error}</div>;
  if (!cardData) return <div>Loading puzzle...</div>;

  const gridSize = cardData.gridSize || 3;
  const pieceWidth = canvasWidth / gridSize;
  const pieceHeight = pieceWidth / aspectRatio;

  return (
    <main style={{ padding: '2rem', textAlign: 'center' }}>
      <h2>Puzzle from {cardData.fromName} to {cardData.toName}</h2>

      {showPreview && (
        <img
          src={cardData.imageUrl}
          alt="Puzzle preview"
          style={{
            width: `${pieceWidth * gridSize}px`,
            height: `${pieceHeight * gridSize}px`,
            opacity: 0.5,
            borderRadius: '4px',
            marginBottom: '1rem',
          }}
        />
      )}
      <div>
        <button onClick={() => setShowPreview(!showPreview)}>
          {showPreview ? 'Hide Preview' : 'Show Preview'}
        </button>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${gridSize}, ${pieceWidth}px)`,
          gridTemplateRows: `repeat(${gridSize}, ${pieceHeight}px)`,
          gap: completed ? '0' : '2px',
          justifyContent: 'center',
          margin: '1rem auto',
          width: `${pieceWidth * gridSize}px`,
          height: `${pieceHeight * gridSize}px`,
        }}
      >
        {slots.map((piece, index) => (
          <div
            key={index}
            onClick={() => handleSwap(index)}
            style={{
              width: pieceWidth,
              height: pieceHeight,
              border: completed ? 'none' : '1px solid #999',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
            }}
          >
            <img
              src={piece.img}
              alt=""
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
          </div>
        ))}
      </div>

      {completed && (
        <>
          <VictoryOverlay />
          <div
            style={{
              fontFamily: cardData.fontStyle || 'serif',
              fontSize: '1.5rem',
              marginTop: '1rem',
              whiteSpace: 'pre-line',
              color: '#333',
            }}
          >
            {cardData.message}
          </div>
        </>
      )}
    </main>
  );
};

export default PlayPuzzleCard;
